/**
 * HeaderBlock Component
 * 
 * Displays restaurant branding - logo, cover photo, and/or name.
 * Used at the top of the menu to establish brand identity.
 */

import React, { memo, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { HeaderBlockProps } from '@/types/menuBuilder';
import { useBranch } from '@/contexts/BranchContext';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Branch } from '@/types/index';

interface HeaderBlockComponentProps {
  props: HeaderBlockProps;
  isPreview?: boolean;
  branch?: Branch;
}

export const HeaderBlock = memo(function HeaderBlock({
  props,
  isPreview = false,
  branch,
}: HeaderBlockComponentProps) {
  const { selectedBranchId } = useBranch(true); // Optional context
  const { branches } = useFilteredBranches();
  
  const currentBranch = useMemo(() => {
    if (branch) return branch;
    return branches.find(b => b.id === selectedBranchId) || null;
  }, [branch, branches, selectedBranchId]);

  const { 
    variant, 
    showLogo, 
    showRestaurantName, 
    showShareButton,
    customHeight,
    backgroundColor,
    overlayOpacity = 0.3,
    textColor = '#ffffff',
    logoPosition = 'center',
    textAlignment = 'center',
    logoSize = 'md',
    customLogoSize,
  } = props;

  // Get restaurant data from branch context
  const restaurantName = currentBranch?.restaurants?.name || currentBranch?.name || 'Restaurant Name';
  const coverUrl = currentBranch?.cover_photo_url;
  const logoUrl = currentBranch?.restaurants?.logo_url;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: restaurantName,
        url: window.location.href,
      });
    }
  };

  // Helper for alignment classes
  const getAlignmentClass = (align: string) => {
    switch (align) {
      case 'left': return 'items-start text-left';
      case 'right': return 'items-end text-right';
      default: return 'items-center text-center';
    }
  };

  const logoSizeClasses: Record<string, string> = {
    sm: 'h-12 w-12',
    md: 'h-16 w-16',
    lg: 'h-24 w-24',
    xl: 'h-32 w-32',
    custom: '',
  };

  const logoStyle = logoSize === 'custom' ? {
    height: customLogoSize,
    width: customLogoSize,
  } : {};

  // Minimal variant - just name
  if (variant === 'minimal') {
    return (
      <div 
        className="flex items-center justify-between p-4"
        style={{ backgroundColor }}
      >
        {showRestaurantName && (
          <h1 className="text-xl font-bold">{restaurantName}</h1>
        )}
        {showShareButton && (
          <Button variant="ghost" size="sm" onClick={handleShare}>
            <Share2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  // Logo-only variant - centered logo without cover
  if (variant === 'logo-only') {
    // Calculate actual logo dimensions
    const getLogoDimension = () => {
      if (logoSize === 'custom' && customLogoSize) {
        return typeof customLogoSize === 'number' ? customLogoSize : parseInt(customLogoSize);
      }
      const sizes: Record<string, number> = { sm: 48, md: 64, lg: 96, xl: 128 };
      return sizes[logoSize] || 96;
    };
    
    const logoDimension = getLogoDimension();
    
    return (
      <div 
        className="relative flex flex-col items-center justify-center"
        style={{ 
          backgroundColor: backgroundColor || 'transparent',
          padding: `${props.logoPadding ?? 4}px`,
        }}
      >
        {showLogo && (
          <div 
            className="overflow-hidden flex items-center justify-center flex-shrink-0"
            style={{
              width: logoDimension,
              height: logoDimension,
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={restaurantName}
                className="h-full w-full object-contain"
              />
            ) : (
              <div 
                className="h-full w-full rounded-full bg-muted/50 flex items-center justify-center"
              >
                <span 
                  className="font-bold text-muted-foreground"
                  style={{ fontSize: logoDimension * 0.4 }}
                >
                  {restaurantName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        )}
        
        {showRestaurantName && (
          <h1 
            className="text-xl font-bold mt-2 text-center"
            style={{ color: textColor !== '#ffffff' ? textColor : undefined }}
          >
            {restaurantName}
          </h1>
        )}
        
        {showShareButton && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="absolute top-2 right-2"
            onClick={handleShare}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        )}
        
        {/* Placeholder for builder mode when nothing is shown */}
        {!showLogo && !showRestaurantName && !isPreview && (
          <div className="py-8 text-muted-foreground text-sm">
            Enable logo or name in properties
          </div>
        )}
      </div>
    );
  }

  // Simple variant - logo and name
  if (variant === 'simple') {
    return (
      <div 
        className="flex items-center gap-4 p-4"
        style={{ backgroundColor }}
      >
        {showLogo && (
          <div 
            className={cn(
              "overflow-hidden rounded-lg bg-muted flex items-center justify-center flex-shrink-0",
              logoSizeClasses[logoSize]
            )}
            style={logoStyle}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={restaurantName}
                className="h-full w-full object-contain"
              />
            ) : (
              <span className="text-2xl font-bold text-muted-foreground">
                {restaurantName.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
        )}
        <div className="flex-1">
          {showRestaurantName && (
            <h1 className="text-2xl font-bold">{restaurantName}</h1>
          )}
        </div>
        {showShareButton && (
          <Button variant="ghost" size="sm" onClick={handleShare}>
            <Share2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  // With-cover variant - full hero with cover image
  return (
    <div 
      className="relative overflow-hidden"
      style={{ 
        height: customHeight || 200,
        backgroundColor: backgroundColor || '#f3f4f6',
      }}
    >
      {/* Cover Image */}
      {coverUrl && (
        <img
          src={coverUrl}
          alt={restaurantName}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black"
        style={{ opacity: overlayOpacity }}
      />
      
      {/* Content */}
      <div 
        className={cn(
          "relative h-full flex flex-col justify-center p-4",
          getAlignmentClass(logoPosition) // Use logo position for main container alignment
        )}
        style={{ color: textColor }}
      >
        {showLogo && (
          <div 
            className={cn(
              "overflow-hidden rounded-full bg-white/20 backdrop-blur flex items-center justify-center mb-3",
              logoSizeClasses[logoSize]
            )}
            style={logoStyle}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={restaurantName}
                className="h-full w-full object-contain"
              />
            ) : (
              <span className="text-3xl font-bold">
                {restaurantName.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
        )}
        
        {showRestaurantName && (
          <h1 
            className={cn(
              "text-3xl font-bold drop-shadow-lg",
              textAlignment === 'left' && "text-left",
              textAlignment === 'center' && "text-center",
              textAlignment === 'right' && "text-right"
            )}
          >
            {restaurantName}
          </h1>
        )}
        
        {showShareButton && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="absolute top-2 right-2 hover:bg-white/20"
            style={{ color: textColor }}
            onClick={handleShare}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Placeholder for builder mode when nothing is shown */}
      {!showLogo && !showRestaurantName && !isPreview && (
        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground bg-muted">
          Enable logo or name in properties
        </div>
      )}
    </div>
  );
});

export default HeaderBlock;
